import { Alert, Button, Group, Stack, Text, Textarea } from '@mantine/core';
import { IconArrowLeft, IconSend } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki } from '../api/odjemalec';
import { casIzMs } from '../oblika/oblika';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { Oznaka } from '../strani/PregledRacunov';
import { Pogovor } from './Pogovor';
import { statusPovratne } from './povratne';

/**
 * Pogovor o povratni informaciji (sem vodi tudi povezava iz e-pošte z odgovorom): sporočila stranke in odgovori naše
 * ekipe, stanje (nastavlja ga skrbništvo) in odpis. Ob odprtju z novim odgovorom se ta označi kot prebran.
 */
export function PodrobnostiPovratneInformacije() {
  const id = Number(useParams().id);
  const odjemalec = useQueryClient();
  const poizvedba = useQuery({
    queryKey: ['povratna', id],
    queryFn: ({ signal }) => podatki(api.GET('/api/povratne-informacije/{id}', { signal, params: { path: { id } } })),
  });
  const [besedilo, setBesedilo] = useState('');
  const [napakaPolja, setNapakaPolja] = useState<string | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [posiljam, setPosiljam] = useState(false);
  const oznaceno = useRef(false);

  const osvezi = async () => {
    await odjemalec.invalidateQueries({ queryKey: ['povratne'] });
    await odjemalec.invalidateQueries({ queryKey: ['povratne-stevilo'] });
  };

  // nov odgovor je s tem, ko ga uporabnik odpre, prebran
  const p = poizvedba.data;
  useEffect(() => {
    if (p?.neprebrano && !oznaceno.current) {
      oznaceno.current = true;
      void podatki(api.POST('/api/povratne-informacije/{id}/prebrano', { params: { path: { id } } }))
        .then(osvezi)
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- enkrat ob prvem prikazu neprebranega
  }, [p?.neprebrano, id]);

  if (!p) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Povratne informacije ni bilo mogoče naložiti"
      />
    );
  }

  const odpisi = async () => {
    setNapaka(null);
    setNapakaPolja(null);
    setPosiljam(true);
    try {
      const n = await podatki(
        api.POST('/api/povratne-informacije/{id}/sporocila', { params: { path: { id } }, body: { besedilo } }),
      );
      odjemalec.setQueryData(['povratna', id], n);
      setBesedilo('');
      await osvezi();
    } catch (e) {
      if (e instanceof NapakaApi && e.polja.besedilo) {
        setNapakaPolja(e.polja.besedilo);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setPosiljam(false);
    }
  };

  const s = statusPovratne(p.status);
  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/povratne-informacije"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Povratne informacije
      </Button>
      <GlavaZaslona naslov="Povratna informacija" opis={`${p.avtor} · ${casIzMs(p.ustvarjeno)}`} />
      <Group gap={6} mb="sm" mt={-8}>
        <Oznaka barva={s.barva}>{s.naziv}</Oznaka>
      </Group>
      <Stack gap="md" maw={860}>
        <Kartica>
          <Pogovor sporocila={p.sporocila ?? []} desno={(m) => !!m.lastno} />
          {!p.odgovorjeno && (
            <Text size="sm" c="dimmed" mt="md">
              Naša ekipa bo odgovorila v aplikaciji, obvestilo o odgovoru boste prejeli po e-pošti.
            </Text>
          )}
        </Kartica>
        <Kartica naslov="Odgovor">
          <Textarea
            aria-label="Vaše sporočilo"
            placeholder={p.status === 'ZAKLJUCENO' ? 'Z odgovorom se pogovor znova odpre.' : 'Vaše sporočilo'}
            value={besedilo}
            onChange={(e) => setBesedilo(e.currentTarget.value)}
            error={napakaPolja}
            autosize
            minRows={3}
            maxRows={12}
          />
          {napaka && (
            <Alert color="red" variant="light" mt="sm" role="alert">
              {napaka}
            </Alert>
          )}
          <Group justify="flex-end" mt="md">
            <Button leftSection={<IconSend size={18} />} onClick={() => void odpisi()} loading={posiljam}>
              Pošlji
            </Button>
          </Group>
        </Kartica>
      </Stack>
    </ZaslonSeznama>
  );
}
